import { ChevronRight } from '@digital-twins/design-system/icons'
import { Button } from '@/components/ui/button'
import CinematicAudienceCard from './CinematicAudienceCard'
import type { CinematicAudienceCardProps } from './CinematicAudienceCard'
import { AudienceCarousel } from './AudienceCarousel'
import { CollapsibleAudienceGrid } from './CollapsibleAudienceGrid'

export interface IndustryData {
  token: string
  name: string
}

interface IndustrySectionProps {
  industry: IndustryData
  audiences: CinematicAudienceCardProps['audience'][]
  formatRelativeTime: (date: string) => string
  onViewAll?: (industry: IndustryData) => void
  renderActionMenu?: (audience: CinematicAudienceCardProps['audience']) => React.ReactNode
  onCardClick?: (audienceId: string) => void
  selectedAudienceId?: string | null
  onBookmarkToggle?: (id: string, nextBookmarked: boolean) => Promise<void>
}

export default function IndustrySection({
  industry,
  audiences,
  formatRelativeTime,
  onViewAll,
  renderActionMenu,
  onCardClick,
  selectedAudienceId,
  onBookmarkToggle,
}: IndustrySectionProps) {
  return (
    <section className="space-y-4" data-testid={`industry-section-${industry.token}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl text-foreground" style={{ letterSpacing: '-0.02em' }}>
          {industry.name}
        </h3>
        {!onCardClick && onViewAll && audiences.length > 3 && (
          <Button
            variant="secondary"
            size="sm"
            className="rounded-xl"
            onClick={() => onViewAll(industry)}
            data-testid={`industry-view-all-${industry.token}`}
          >
            View all
            <ChevronRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <div className="md:hidden">
        <AudienceCarousel>
          {audiences.map((audience) => (
            <CinematicAudienceCard
              key={audience.id}
              audience={audience}
              provider={null}
              formatRelativeTime={formatRelativeTime}
              actionMenu={renderActionMenu?.(audience)}
              onClick={onCardClick ? () => onCardClick(audience.id) : undefined}
              selected={selectedAudienceId === audience.id}
              onBookmarkToggle={onBookmarkToggle}
            />
          ))}
        </AudienceCarousel>
      </div>

      <div className="hidden md:block">
        <CollapsibleAudienceGrid
          cards={audiences.map((audience) => (
            <CinematicAudienceCard
              key={audience.id}
              audience={audience}
              provider={null}
              formatRelativeTime={formatRelativeTime}
              actionMenu={renderActionMenu?.(audience)}
              onClick={onCardClick ? () => onCardClick(audience.id) : undefined}
              selected={selectedAudienceId === audience.id}
              onBookmarkToggle={onBookmarkToggle}
            />
          ))}
        />
      </div>
    </section>
  )
}
